import { Box, Link, Stack } from '@mui/material';
import { compact } from 'lodash';
import { Link as RouterLink } from 'react-router-dom';

import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { ApplicationConfigurationFormFields } from '@/pages/admin/ApplicationConfigurationForm/ApplicationConfigurationFormFields';
import {
  AugmentedUpdateTenantApplicationConfigurationInput,
  McpAccess,
  TenantApplicationConfigurationNonSsoLoginMechanism,
  TenantApplicationConfigurationUserSessionInactivityTimeout,
} from '@/types/schema';

import { useUpdateTenantConfigurationFormMutation } from './graphql/TenantConfigurationForm.generated';
import { useTenantConfigurationFormDefaultValues } from './hooks/useTenantConfigurationFormDefaultValues';
import { TenantConfigurationFormShape } from './TenantConfigurationForm.constants';

interface TenantConfigurationFormProps {
  isOrgMember?: boolean;
}

export function TenantConfigurationForm({
  isOrgMember = false,
}: TenantConfigurationFormProps) {
  const formMethods = useForm<TenantConfigurationFormShape>({
    defaultValues: {
      collaboratorDomainAllowList: [],
      disableAiCapabilities: false,
      disableAiInsights: false,
      enableEmployeeIdpId: false,
      mcpAccess: McpAccess.Off,
      smartReviewEnabled: true,
      userSessionInactivityTimeout:
        TenantApplicationConfigurationUserSessionInactivityTimeout.Off,
      nonSsoLoginMechanism:
        TenantApplicationConfigurationNonSsoLoginMechanism.MagicLink,
    },
  });
  const { createSuccessFeedback, showFeedback } = useFeedback();
  const { data: initialData } = useTenantConfigurationFormDefaultValues(
    formMethods.reset
  );
  const [updateTenantConfiguration, { loading }] =
    useUpdateTenantConfigurationFormMutation({
      onCompleted: createSuccessFeedback('Successfully updated configuration'),
      onError: (error) => {
        const userFacingMessages = getUserFacingErrorMessages(error);
        if (userFacingMessages.length > 0) {
          showFeedback(userFacingMessages.join('. '));
        } else {
          showFeedback(
            'There was an error updating the configuration. Please try again.'
          );
        }
      },
    });

  const onSubmit = formMethods.handleSubmit((formValues) => {
    const tacId = initialData?.tenantApplicationConfiguration?.id;
    if (!tacId) {
      throw new Error('No tenant configuration id found');
    }

    const domainValues = compact(
      formValues.collaboratorDomainAllowList.map((d) => d.value.trim())
    );

    const input: AugmentedUpdateTenantApplicationConfigurationInput = {
      id: tacId,
      update: {
        collaboratorDomainAllowList: domainValues,
        // Clear the list when no domains are provided so the backend
        // removes any previously stored entries.
        clearCollaboratorDomainAllowList: domainValues.length === 0,
        disableAiCapabilities: formValues.disableAiCapabilities,
        disableAiInsights: formValues.disableAiInsights,
        enableEmployeeIdpID: formValues.enableEmployeeIdpId,
        mcpAccess: formValues.mcpAccess,
        smartReviewEnabled: formValues.smartReviewEnabled,
        userSessionInactivityTimeout: formValues.userSessionInactivityTimeout,
        nonSSOLoginMechanism: formValues.nonSsoLoginMechanism,
      },
    };
    return updateTenantConfiguration({ variables: { input } });
  });

  if (isOrgMember) {
    return (
      <Stack spacing={2} maxWidth={400}>
        <Callout severity="info-high">
          This tenant is part of an organization. Application configurations are
          managed at the organization level.{' '}
          <Link component={RouterLink} to="/admin/organizations">
            Go to Organizations
          </Link>{' '}
          to manage them there.
        </Callout>
      </Stack>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <Box component="form" onSubmit={onSubmit}>
        <ApplicationConfigurationFormFields
          configurationLevel="tenant"
          loading={loading}
        />
      </Box>
    </FormProvider>
  );
}
